/**
 * Roll-up Table Widget (Story 40-5 — Epic 40)
 *
 * A roll-up's per-client breakdown: one row per member client (its value for each
 * configured metric) + a TOTALS/AVERAGES footer (the engine always appends it as
 * the LAST row; we style it bold with a top border). Consumes the shared
 * `TabularData` envelope; uses the standard chart-widget shell for loading/error/
 * empty states. The Roll-up Table has no single-client meaning — its only data
 * source is the cross-client populate (populateRollupWidgets).
 */

import type { WidgetConfig } from "../../../lib/templateUtils";
import type { TabularData, WidgetDataEnvelope } from "../../../lib/widgetData";
import { isTabularData } from "../../../lib/widgetData";
import { useChartWidgetShell, ChartMessage } from "./chartWidgetShell";

interface RollupTableWidgetProps {
  widget: WidgetConfig;
  data?: WidgetDataEnvelope<TabularData>;
}

function isNumeric(v: unknown): v is number {
  return typeof v === "number";
}

function formatCell(v: string | number | undefined): string {
  if (v == null || v === "") return "—";
  return isNumeric(v) ? new Intl.NumberFormat("en-US").format(v) : String(v);
}

export function RollupTableWidget({ widget, data }: RollupTableWidgetProps) {
  const { shell } = useChartWidgetShell(widget.config);

  if (data?.loading) {
    return shell(
      "rollup-table",
      widget.title,
      <div className="flex-1 space-y-1">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-6 animate-pulse rounded bg-muted" />
        ))}
      </div>,
    );
  }

  if (data?.error) {
    return shell(
      "rollup-table",
      widget.title,
      <ChartMessage role="alert">{data.error}</ChartMessage>,
      { centered: true },
    );
  }

  const tableData = data?.data;
  if (!tableData || !isTabularData(tableData) || tableData.rows.length === 0) {
    return shell(
      "rollup-table",
      widget.title,
      <ChartMessage testId="rollup-table-no-data">No member data for this roll-up</ChartMessage>,
      { centered: true },
    );
  }

  const columns = Object.keys(tableData.rows[0]!);
  // The engine appends the totals/averages row LAST.
  const footer = tableData.rows[tableData.rows.length - 1]!;
  const bodyRows = tableData.rows.slice(0, -1);

  const body = (
    <div className="min-w-0 flex-1 overflow-x-auto" style={{ overscrollBehavior: "contain" }}>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-border">
            {columns.map((col, idx) => (
              <th
                key={col}
                scope="col"
                className={`px-2.5 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground ${idx === 0 ? "text-left" : "text-right"}`}
                data-testid={`rollup-table-header-${col}`}
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {bodyRows.map((row, rowIdx) => (
            <tr key={rowIdx} className="border-b border-border/40" data-testid="rollup-table-row">
              {columns.map((col, colIdx) => (
                <td
                  key={col}
                  className={`max-w-[220px] truncate px-2.5 py-1.5 text-sm text-foreground ${colIdx === 0 ? "text-left" : "text-right font-mono tabular-nums"}`}
                  title={typeof row[col] === "string" ? String(row[col]) : undefined}
                >
                  {formatCell(row[col])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-border bg-muted/40 font-semibold" data-testid="rollup-table-footer">
            {columns.map((col, colIdx) => (
              <td
                key={col}
                className={`px-2.5 py-1.5 text-sm text-foreground ${colIdx === 0 ? "text-left" : "text-right font-mono tabular-nums"}`}
              >
                {formatCell(footer[col])}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );

  return shell("rollup-table", widget.title, body);
}
